import { adminService } from "./service";

/** Terminal values the backend writes to `flyer_auto_status` once a background
 *  flyer job stops running. Anything else ("running", missing) means keep polling. */
export type FlyerTerminalStatus = "done" | "failed" | "skipped";

export interface PollFlyerStatusOptions {
  /** Called once with the freshly fetched deal doc when the job reaches a terminal state. */
  onTerminal: (deal: any, status: FlyerTerminalStatus) => void;
  /** Called if the deadline is hit with no terminal status — the job may well still
   *  be running server-side, so callers should not report this as a failure. */
  onTimeout?: () => void;
  /** Override the polling budget (ms). Defaults to 12 minutes. */
  deadlineMs?: number;
  /** The deal's typed collection, when the caller already knows it (the Deals
   *  page always does). Fetches via getDealInCollection instead of the
   *  shadow-first getDeal(id) — a promoted deal has a doc in both the shadow
   *  `deals` collection and its typed one, and getDeal(id) always returns the
   *  shadow copy, which has AI-derived fields stripped after promotion. Omit
   *  only when the collection genuinely isn't known. */
  collection?: string;
}

/**
 * Polls a deal doc for `flyer_auto_status` until the background flyer job
 * (fired by the flyer-replace or generate-ai-flyer endpoints) reaches a terminal
 * state, so the admin sees a live "Generating…" indicator instead of the button
 * just going back to normal while nothing has actually finished yet.
 *
 * The deadline must exceed the backend's own generation budget or this reports
 * "failed" on jobs that are still running: fal_client._call_queue allows up to
 * max_wait=600s (10min) for the FAL queue call alone, on top of the Grok/Gemini
 * extraction pass that runs before it. 12 minutes gives that a margin instead of
 * a hair-trigger 2-minute window.
 */
export async function pollFlyerStatus(dealId: string, opts: PollFlyerStatusOptions): Promise<void> {
  const deadline = Date.now() + (opts.deadlineMs ?? 12 * 60 * 1000);
  let consecutiveErrors = 0;
  while (Date.now() < deadline) {
    // Back off on repeated network/API errors instead of hammering every
    // 3s through an outage — cap at 15s between attempts.
    const delay = Math.min(3000 * 2 ** consecutiveErrors, 15000);
    await new Promise((r) => setTimeout(r, delay));
    try {
      const deal = opts.collection
        ? await adminService.getDealInCollection(opts.collection, dealId)
        : await adminService.getDeal(dealId);
      consecutiveErrors = 0;
      const status = deal?.flyer_auto_status;
      if (status === "done" || status === "failed" || status === "skipped") {
        opts.onTerminal(deal, status);
        return;
      }
    } catch {
      // transient — keep polling (with backoff) until deadline
      consecutiveErrors += 1;
    }
  }
  opts.onTimeout?.();
}
